import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import WelcomeCard from './WelcomeCard';
import PartnerSelectionModal from './PartnerSelectionModal';

const MenuHome = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);

  const mainCategories = [
    {
      id: 'gestione-dipendenti',
      title: t('adminHome.categories.dipendenti.title', { defaultValue: 'DIPENDENTI' }),
      subtitle: t('adminHome.categories.dipendenti.subtitle', { defaultValue: 'Presenze, Documenti & Turni' }),
      path: '/home-dashboard',
      icon: 'Users',
      color: 'from-blue-500 to-indigo-600',
      glow: 'group-hover:shadow-blue-500/20',
      description: t('adminHome.categories.dipendenti.description', { defaultValue: 'Gestione del personale, contratti di lavoro, documenti personali, presenze, turni e report delle timbrature.' })
    },
    {
      id: 'mia-azienda',
      title: t('adminHome.categories.azienda.title', { defaultValue: 'AZIENDA' }),
      subtitle: t('adminHome.categories.azienda.subtitle', { defaultValue: 'Profilo & Documenti' }),
      path: '/documents-company',
      icon: 'Building2',
      color: 'from-emerald-500 to-teal-700',
      glow: 'group-hover:shadow-emerald-500/20',
      description: t('adminHome.categories.azienda.description', { defaultValue: 'Archivia e gestisci i documenti ufficiali societari, i contratti di locazione e la burocrazia interna.' })
    },
    {
      id: 'miei-partner',
      title: t('adminHome.categories.partner.title', { defaultValue: 'PARTNER' }),
      subtitle: t('adminHome.categories.partner.subtitle', { defaultValue: 'Clienti & Fornitori' }),
      path: '/my-partners',
      icon: 'Handshake',
      color: 'from-amber-500 to-orange-600',
      glow: 'group-hover:shadow-amber-500/20',
      description: t('adminHome.categories.partner.description', { defaultValue: 'Anagrafica completa dei partner commerciali, contratti di fornitura, fatture e storico ordini.' })
    }
  ];

  const handleCategoryClick = (cat) => {
    if (cat.id === 'miei-partner') {
      setIsPartnerModalOpen(true);
    } else {
      navigate(cat.path);
    }
  };

  return (
    <div className="w-full animate-in fade-in slide-in-from-bottom-6 duration-1000 ease-out space-y-12 lg:space-y-20">

      {/* ORIGINALNI WELCOME CARD */}
      <WelcomeCard />

      {/* GRID SISTEM KATEGORIJA (3 VELIKA DUGMETA) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
        {mainCategories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => handleCategoryClick(cat)}
            className={`group relative flex flex-col bg-card rounded-[2.5rem] border border-border/40 overflow-hidden transition-all duration-500 text-left hover:border-primary/40 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] ${cat.glow}`}
          >
            {/* Dekorativni gradijent na vrhu */}
            <div className={`h-2 w-full bg-gradient-to-r ${cat.color} opacity-80 group-hover:opacity-100 transition-opacity`} />

            <div className="p-8 lg:p-10 flex flex-col h-full w-full">
              {/* IKONA I STRIJELICA */}
              <div className="flex justify-between items-start mb-12">
                <div className={`w-16 h-16 rounded-[1.5rem] bg-gradient-to-br ${cat.color} flex items-center justify-center text-white shadow-2xl transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 ease-spring`}>
                  <Icon name={cat.icon} size={32} strokeWidth={2.5} />
                </div>

                <div className="w-12 h-12 rounded-2xl bg-muted/50 backdrop-blur-sm border border-white/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground group-hover:rotate-45 transition-all duration-500">
                  <Icon name="ArrowUpRight" size={20} strokeWidth={3} />
                </div>
              </div>

              {/* TEKSTUALNI SADRŽAJ */}
              <div className="flex-grow">
                <h3 className="text-4xl font-black tracking-tighter text-foreground mb-1 group-hover:translate-x-1 transition-transform duration-300">
                  {cat.title}
                </h3>
                <h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-primary/70 mb-4">
                  {cat.subtitle}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed font-medium opacity-70 group-hover:opacity-100 transition-opacity">
                  {cat.description}
                </p>
              </div>

              {/* DONJI ACTION BAR */}
              <div className="mt-10 flex items-center justify-between overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-[1px] bg-primary/30 group-hover:w-10 group-hover:bg-primary transition-all duration-500" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">
                    {t('adminHome.common.accessSection', { defaultValue: 'Accedi alla sezione' })}
                  </span>
                </div>
                <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${cat.color} animate-pulse shadow-[0_0_10px_rgba(0,0,0,0.5)]`} />
              </div>
            </div>

            {/* Pozadinski dekorativni krugovi */}
            <div className={`absolute -bottom-16 -right-16 w-48 h-48 bg-gradient-to-br ${cat.color} opacity-[0.02] rounded-full blur-3xl group-hover:opacity-[0.1] group-hover:scale-150 transition-all duration-1000`} />
          </button>
        ))}
      </div>

      {/* POPUP MODAL ZA IZBOR PARTNERA */}
      <PartnerSelectionModal 
        isOpen={isPartnerModalOpen} 
        onClose={() => setIsPartnerModalOpen(false)} 
      />
    </div>
  );
};

export default MenuHome;